<script>
// Styles
// import './VTabs.sass'

// Components
import TabsBar from "./TabsBar";
import TabsItems from "./TabsItems";
import TabsSlider from "./TabsSlider";

// Mixins
import Colorable from "@mixins/colorable";
import Proxyable from "@mixins/proxyable";

// Directives
import Resize from "@/core/directives/resize";

// Utilities
import { convertToUnit } from "@/core/util/helpers";
import mixins from "@/core/util/mixins";

const baseMixins = mixins(Colorable, Proxyable);

export default baseMixins.extend({
  name: "Tabs",

  directives: {
    Resize
  },

  props: {
    activeClass: {
      type: String,
      default: "tab_active"
    },
    backgroundColor: String,
    height: {
      type: [Number, String],
      default: undefined
    },
    hideSlider: Boolean,
    optional: Boolean,
    right: Boolean,
    sliderColor: String,
    sliderSize: {
      type: [Number, String],
      default: 2
    },
    vertical: Boolean
  },

  data() {
    return {
      resizeTimeout: 0,
      slider: {
        height: null,
        left: null,
        right: null,
        top: null,
        width: null
      },
      transitionTime: 300
    };
  },

  computed: {
    classes() {
      return {
        "tabs_align-with-title": this.alignWithTitle,
        tabs_centered: this.centered,
        "tabs_fixed-tabs": this.fixedTabs,
        tabs_grow: this.grow,
        "tabs_icons-and-text": this.iconsAndText,
        tabs_right: this.right,
        tabs_vertical: this.vertical
      };
    },
    isReversed() {
      return this.vertical;
    },
    sliderStyles() {
      return {
        height: convertToUnit(this.slider.height),
        left: this.isReversed ? undefined : convertToUnit(this.slider.left),
        right: this.isReversed ? convertToUnit(this.slider.right) : undefined,
        top: this.vertical ? convertToUnit(this.slider.top) : undefined,
        transition: this.slider.left != null ? null : "none",
        width: convertToUnit(this.slider.width)
      };
    },
    computedColor() {
      if (this.color) return this.color;
      else return "primary";
    }
  },

  watch: {
    right: "callSlider",
    vertical: "callSlider"
  },

  mounted() {
    this.$nextTick(() => {
      window.setTimeout(this.callSlider, 30);
    });
  },

  methods: {
    callSlider() {
      if (
        this.hideSlider ||
        !this.$refs.items ||
        !this.$refs.items.selectedItems.length
      ) {
        this.slider.width = 0;
        return false;
      }

      this.$nextTick(() => {
        // Give screen time to paint
        const activeTab = this.$refs.items.selectedItems[0];
        /* istanbul ignore if */
        if (!activeTab || !activeTab.$el) {
          this.slider.width = 0;
          this.slider.left = 0;
          return;
        }
        const el = activeTab.$el;

        this.slider = {
          height: !this.vertical ? Number(this.sliderSize) : el.scrollHeight,
          left: this.vertical ? 0 : el.offsetLeft,
          right: this.vertical ? 0 : el.offsetLeft + el.offsetWidth,
          top: el.offsetTop,
          width: this.vertical ? Number(this.sliderSize) : el.scrollWidth
        };
      });

      return true;
    },
    genBar(items, slider) {
      const data = {
        style: {
          height: convertToUnit(this.height)
        },
        props: {
          activeClass: this.activeClass,
          mandatory: !this.optional,
          value: this.internalValue
        },
        on: {
          "call:slider": this.callSlider,
          change: val => {
            this.internalValue = val;
          }
        },
        ref: "items"
      };

      this.setTextColor(this.computedColor, data);
      this.setBackgroundColor(this.backgroundColor, data);

      return this.$createElement(TabsBar, data, [
        this.genSlider(slider),
        items
      ]);
    },
    genItems(items, item) {
      // If user provides items
      // opt to use theirs
      if (items) return items;

      // If no tabs are provided
      // render nothing
      if (!item.length) return null;

      return this.$createElement(
        TabsItems,
        {
          props: {
            value: this.internalValue
          },
          on: {
            change: val => {
              this.internalValue = val;
            }
          }
        },
        item
      );
    },
    genSlider(slider) {
      if (this.hideSlider) return null;

      if (!slider) {
        slider = this.$createElement(TabsSlider, {
          props: { color: this.sliderColor }
        });
      }

      return this.$createElement(
        "div",
        {
          staticClass: "tabs-slider-wrapper",
          style: this.sliderStyles
        },
        [slider]
      );
    },
    onResize() {
      if (this._isDestroyed) return;

      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(this.callSlider, 0);
    },
    parseNodes() {
      let items = null;
      let slider = null;
      const item = [];
      const tab = [];
      const slot = this.$slots.default || [];
      const length = slot.length;

      for (let i = 0; i < length; i++) {
        const vnode = slot[i];

        if (vnode.componentOptions) {
          switch (vnode.componentOptions.Ctor.options.name) {
            case "TabsSlider":
              slider = vnode;
              break;
            case "TabsItems":
              items = vnode;
              break;
            case "TabItem":
              item.push(vnode);
              break;
            // case 'Tab' - intentionally omitted
            default:
              tab.push(vnode);
          }
        } else {
          tab.push(vnode);
        }
      }

      /**
       * tab: array of `v-tab`
       * slider: single `TabsSlider`
       * items: single `v-tabs-items`
       * item: array of `v-tab-item`
       */
      return { tab, slider, items, item };
    }
  },

  render(h) {
    const { tab, slider, items, item } = this.parseNodes();

    return h(
      "div",
      {
        staticClass: "tabs",
        class: this.classes,
        directives: [
          {
            name: "resize",
            modifiers: { quiet: true },
            value: this.onResize
          }
        ]
      },
      [this.genBar(tab, slider), this.genItems(items, item)]
    );
  }
});
</script>
